import { showCategoryModal } from '@/components/cashflow/categories/CategoryModal';
import InstitutionLogo from '@/components/cashflow/InstitutionLogo';
import { showTagModal } from '@/components/cashflow/tags/TagModal';
import TagValues from '@/components/cashflow/tags/TagValues';
import SplitTransactionEntry from '@/components/cashflow/transactions/SplitTransactionEntry';
import { TransactionEntryDetailRow } from '@/components/cashflow/transactions/TransactionEntryDetailRow';
import useCategoryOptions from '@/components/cashflow/useCategoryOptions';
import useMerchantSelect from '@/components/cashflow/useMerchantSelect';
import useTagOptions from '@/components/cashflow/useTagOptions';
import { useUpdateTags } from '@/components/cashflow/useUpdateTags';
import useUpdateTransactionEntry from '@/components/cashflow/useUpdateTransactionEntry';
import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { ShowDrawerFcn } from '@/components/common/DrawerManager';
import ErrorMessage from '@/components/common/ErrorMessage';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    ArrowRight,
    InfoCircle,
    Share06,
    Trash01,
} from '@/components/common/icons';
import { ArrowDecisionUp } from '@/components/common/icons/custom/ArrowDecisionUp';
import InputField from '@/components/common/InputField';
import LoadingWrapper from '@/components/common/LoadingWrapper';
import { InnerConfirmModal } from '@/components/common/modals/ConfirmModal';
import Modal from '@/components/common/modals/Modal';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { useToasts } from '@/components/common/Toasts';
import { Paragraph, Title } from '@/components/common/Typography';
import ModalManager, { useModal } from '@/components/ModalManager';
import {
    useTracking,
    useTrackModalShown,
} from '@/services/analytics/useTracking';
import { toDollars } from '@/util/currency';
import { logError } from '@/util/logger';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    ModalCloseButton,
    Text,
    Textarea,
    Tooltip,
} from '@chakra-ui/react';
import { TransactionEntryCountForMerchantDocument } from '@common/graphql/generated';
import {
    CashflowTransactionEntry,
    useTransactionEntry,
} from '@common/react/cashflow';
import { useDebouncedCallback } from '@common/react/debounce';
import { TOASTS } from '@common/toasts';
import { ALL_TIME_DATE_RANGE, DT, dt } from '@common/util/dateTime';
import pluralize from '@common/util/pluralize';
import { useRouter } from 'next/router';
import { useState } from 'react';

export interface TransactionEntryDetailsModalProps {
    transactionEntryId?: string;
    transactionEntry?: CashflowTransactionEntry;
    showDrawer: ShowDrawerFcn;
}

type SaveNoteStatus = 'saved' | 'saving' | 'error';

export const showTransactionEntryDetailsModal = (
    props: TransactionEntryDetailsModalProps
) => ModalManager.show(TransactionEntryDetailsModal, props);

const TransactionEntryDetailsModal =
    ModalManager.create<TransactionEntryDetailsModalProps>(
        ({ transactionEntry, transactionEntryId, showDrawer }) => {
            useTrackModalShown('Transaction Entry Details');

            const modal = useModal();
            const [isDeleting, setIsDeleting] = useState<boolean>(false);
            const [isSplitting, setIsSplitting] = useState<boolean>(false);
            const { loading, transactionEntry: transactionEntryFromId } =
                useTransactionEntry(transactionEntryId);

            if (transactionEntryFromId && !transactionEntry) {
                transactionEntry = transactionEntryFromId;
            }

            return (
                <Modal
                    isOpen={modal.visible}
                    onClose={modal.remove}
                    isCentered={true}
                    unstyled={!isDeleting}
                    size={isDeleting ? 'sm' : isSplitting ? '4xl' : 'xl'}
                    isFullScreen
                >
                    <LoadingWrapper isLoading={loading}>
                        {transactionEntry ? (
                            isDeleting ? (
                                <DeleteTransactionEntry
                                    transactionEntry={transactionEntry}
                                    onClickCancel={() => setIsDeleting(false)}
                                />
                            ) : isSplitting ? (
                                <SplitTransactionEntry
                                    transactionEntry={transactionEntry}
                                    onClickBack={() => setIsSplitting(false)}
                                />
                            ) : (
                                <TransactionEntryDetails
                                    transactionEntry={transactionEntry}
                                    showDrawer={showDrawer}
                                    onClickDelete={() => setIsDeleting(true)}
                                    onClickSplit={() => setIsSplitting(true)}
                                />
                            )
                        ) : (
                            <ErrorMessage message="Failed to load transaction. Please try again." />
                        )}
                    </LoadingWrapper>
                </Modal>
            );
        }
    );

const DeleteTransactionEntry = ({
    transactionEntry,
    onClickCancel,
}: {
    transactionEntry: CashflowTransactionEntry;
    onClickCancel: () => void;
}) => {
    const modal = useModal();
    const updateTransactionEntry = useUpdateTransactionEntry(transactionEntry);

    return (
        <InnerConfirmModal
            title="Delete Transaction"
            description="Are you sure you want to delete this transaction?"
            buttonText="Yes, delete"
            onConfirm={async () => {
                await updateTransactionEntry({
                    variables: {
                        transactionEntryId: transactionEntry.id,
                        deletedAt: new Date().toISOString(),
                    },
                });
                modal.remove();
            }}
            onSecondaryButton={onClickCancel}
            onClickPrimaryEvent="Delete Transaction Confirm Button Clicked"
            onClickSecondaryEvent="Cancel Delete Transaction Button Clicked"
        />
    );
};

const TransactionEntryDetails = ({
    transactionEntry,
    onClickDelete,
    onClickSplit,
    showDrawer,
}: {
    transactionEntry: CashflowTransactionEntry;
    showDrawer: ShowDrawerFcn;
    onClickDelete: VoidFunction;
    onClickSplit: VoidFunction;
}) => {
    const { showSuccessToast, showErrorToast } = useToasts();
    const router = useRouter();
    const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
    const modal = useModal();
    const [categoryId, setCategoryId] = useState<string>(
        transactionEntry.category.id
    );
    const [tagNames, setTagNames] = useState<string[]>(
        transactionEntry.tags.map((tag) => tag.name)
    );
    const [notes, setNotes] = useState<string | null | undefined>(
        transactionEntry.notes
    );
    const [saveNoteStatus, setSaveNoteStatus] =
        useState<SaveNoteStatus>('saved');

    const [date, setDate] = useState<DT>(dt(transactionEntry.date));
    const [isHidden, setIsHidden] = useState<boolean>(
        transactionEntry.isHidden
    );
    const categoryOptions = useCategoryOptions();
    const tagOptions = useTagOptions();
    const { track } = useTracking();

    const {
        handleLoadOptions,
        handleCreateOption,
        handleChange,
        merchantValue,
    } = useMerchantSelect({
        merchant: transactionEntry.merchant,
        showLogo: true,
    });

    const { data: similarTransactionData } = useQuery(
        TransactionEntryCountForMerchantDocument,
        {
            ...(transactionEntry.merchant
                ? { variables: { merchantId: transactionEntry.merchant.id } }
                : { skip: true }),
            fetchPolicy: 'network-only',
        }
    );

    const updateTags = useUpdateTags(transactionEntry);

    const similarTransactionCount =
        similarTransactionData?.transactionEntryCountForMerchant || 0;

    const updateTransactionEntry = useUpdateTransactionEntry(transactionEntry);

    const hideTransactionEntry = (isHidden: boolean) => {
        setIsHidden(isHidden);
        updateTransactionEntry({
            variables: {
                transactionEntryId: transactionEntry.id,
                isHidden,
            },
        });
    };

    const onChangeCategory = (option: StringOption) => {
        setCategoryId(option.value);
        updateTransactionEntry({
            variables: {
                transactionEntryId: transactionEntry.id,
                categoryId: option.value,
            },
        });
    };

    const onChangeTags = async (tagNames: string[]) => {
        track('Tags Updated', {
            tag_names: tagNames,
            location: 'TransactionEntryDetailsModal',
        });
        setTagNames(tagNames);
        await updateTags({
            variables: {
                transactionEntryId: transactionEntry.id,
                tagNames,
            },
        });
    };

    const handleUpdateNotes = useDebouncedCallback(async () => {
        try {
            await updateTransactionEntry({
                variables: {
                    transactionEntryId: transactionEntry.id,
                    notes: notes,
                },
            });
            setSaveNoteStatus('saved');
        } catch (e) {
            logError(e);
            setSaveNoteStatus('error');
            showErrorToast(TOASTS.updateTransactionEntryError);
        }
    }, 2000);

    const merchantId = transactionEntry.merchant?.id;
    const isPending = transactionEntry.isPending;

    return (
        <Box overflowY="scroll">
            <ModalCloseButton size="lg" color="textMuted" zIndex="modal" />
            <Tooltip label="Share transaction">
                <Box
                    zIndex="modal"
                    position="absolute"
                    top={3}
                    right={13}
                    p={1.5}
                    borderRadius="md"
                    cursor="pointer"
                    fontSize="sm"
                    {...hoverStyleProps({
                        bg: 'blackAlpha.100',
                    })}
                    onClick={async () => {
                        try {
                            track('Share Transaction Entry Button Clicked');
                            const query = new URLSearchParams({
                                ...router.query,
                                modal: 'transactionEntryDetail',
                                id: transactionEntry.id,
                            }).toString();

                            await navigator.clipboard.writeText(
                                `${window.location.origin}${window.location.pathname}?${query}`
                            );
                            showSuccessToast(TOASTS.copySharingLinkSuccess);
                        } catch {
                            showErrorToast(TOASTS.copySharingLinkError);
                        }
                    }}
                >
                    <Share06 color="gray.500" />
                </Box>
            </Tooltip>
            <Box
                top={0}
                pt={8}
                px={8}
                pb={4}
                bg="white"
                zIndex="sticky"
                borderBottom="1px solid"
                borderColor="border"
                position="sticky"
                borderTopRightRadius={6}
                borderTopLeftRadius={6}
            >
                <FlexRow gap={2} align="center">
                    {merchantValue?.label
                        ? merchantValue.label
                        : 'Transaction details'}
                    <Text color="gray.500" ml={2} mb="1px">
                        {date.format('MMMM D, YYYY')}
                    </Text>
                </FlexRow>
                <Title my={2}>
                    {toDollars(transactionEntry.amount, {
                        withCents: true,
                        alwaysSigned: true,
                    })}
                </Title>

                {isPending && (
                    <Alert
                        status="info"
                        borderRadius="md"
                        my={2}
                        p={2}
                        fontSize="sm"
                        colorScheme="gray"
                        gap={2}
                    >
                        <InfoCircle size="sm" />
                        This transaction is pending. Once finalized, you can
                        update the details.
                    </Alert>
                )}

                <FlexRow gap={5}>
                    {!transactionEntry.isPending && (
                        <Text
                            fontSize="md"
                            textDecoration="underline"
                            cursor="pointer"
                            fontWeight="medium"
                            display="flex"
                            alignItems="center"
                            onClick={() => {
                                track('Split Transaction Entry Button Clicked');
                                onClickSplit();
                            }}
                            gap={1}
                        >
                            <ArrowDecisionUp size="md" />
                            Split
                        </Text>
                    )}
                    {similarTransactionCount > 1 && merchantId && (
                        <Text
                            fontSize="md"
                            textDecoration="underline"
                            cursor="pointer"
                            fontWeight="medium"
                            onClick={() => {
                                router.push({
                                    pathname: '/cashflow/transactions',
                                    query: {
                                        merchantId,
                                        ...ALL_TIME_DATE_RANGE,
                                    },
                                });
                                modal.remove();
                            }}
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            {similarTransactionCount.toLocaleString()} similar{' '}
                            {pluralize(
                                'transaction',
                                similarTransactionCount,
                                false
                            )}
                            <ArrowRight size="xs" />
                        </Text>
                    )}
                </FlexRow>
            </Box>
            <Box px={8} pb={8}>
                <TransactionEntryDetailRow label="Subcategory" mt={4}>
                    {isPending ? (
                        <Paragraph>{transactionEntry.category.name}</Paragraph>
                    ) : (
                        <InputField
                            type="fancySelect"
                            name="category"
                            flexGrow={1}
                            maxW={300}
                            fancySelectOptions={{
                                isClearable: false,
                                fullScreenPlacement: 'right',
                                menuWidth: 300,
                                cta: {
                                    content: 'Create new subcategory',
                                    iconName: 'Plus',
                                    onClick: () => {
                                        showCategoryModal({
                                            onSuccess: ({ id, name }) => {
                                                onChangeCategory({
                                                    label: name,
                                                    value: id,
                                                });
                                            },
                                        });
                                    },
                                },
                            }}
                            value={categoryId}
                            onChange={onChangeCategory}
                            options={categoryOptions}
                        />
                    )}
                </TransactionEntryDetailRow>
                <TransactionEntryDetailRow label="Tags">
                    {isPending ? (
                        transactionEntry.tags.length ? (
                            <TagValues
                                tagNames={transactionEntry.tags.map(
                                    (t) => t.name
                                )}
                            />
                        ) : (
                            <Paragraph>Untagged</Paragraph>
                        )
                    ) : (
                        <InputField
                            type="fancySelect"
                            name="tags"
                            flexGrow={1}
                            maxW={300}
                            fancySelectOptions={{
                                isClearable: false,
                                isMulti: true,
                                controlShouldRenderValue: true,
                                placeholder: 'Add tags...',
                                menuWidth: 300,
                                fullScreenPlacement: 'right',
                                cta: {
                                    content: 'Create new tag',
                                    iconName: 'Plus',
                                    onClick: () => {
                                        showTagModal({
                                            onSuccess: async (tag) =>
                                                await onChangeTags([
                                                    ...tagNames,
                                                    tag.name,
                                                ]),
                                        });
                                    },
                                },
                            }}
                            value={tagNames}
                            onChange={async (options: StringOption[]) =>
                                await onChangeTags(
                                    options.map((option) => option.value)
                                )
                            }
                            options={tagOptions}
                        />
                    )}
                </TransactionEntryDetailRow>
                <TransactionEntryDetailRow label="Merchant">
                    {isPending ? (
                        <Paragraph>{transactionEntry.merchant?.name}</Paragraph>
                    ) : transactionEntry.financialEntity.goal ? (
                        <Paragraph>Plenty</Paragraph>
                    ) : (
                        <InputField
                            type="fancySelect"
                            name="merchant"
                            maxW={300}
                            fancySelectOptions={{
                                placeholder: isMenuOpen ? (
                                    'Search merchants...'
                                ) : (
                                    <Text color="gray.400">
                                        Add merchant...
                                    </Text>
                                ),
                                fullScreenPlacement: 'right',
                                isClearable: false,
                                onMenuOpen: () => setMenuOpen(true),
                                onMenuClose: () => setMenuOpen(false),
                                defaultOptions: true,
                                loadOptions: handleLoadOptions,
                                onCreateOption: async (name) => {
                                    const merchant =
                                        await handleCreateOption(name);

                                    if (merchant) {
                                        await updateTransactionEntry({
                                            variables: {
                                                transactionEntryId:
                                                    transactionEntry.id,
                                                merchantId: merchant.id,
                                            },
                                        });
                                    }
                                },
                                formatCreateLabel: (inputValue) =>
                                    `Create merchant "${inputValue}"`,
                            }}
                            value={merchantValue}
                            onChange={async (option: StringOption) => {
                                handleChange(option);
                                await updateTransactionEntry({
                                    variables: {
                                        transactionEntryId: transactionEntry.id,
                                        merchantId: option.value,
                                    },
                                });
                            }}
                        />
                    )}
                </TransactionEntryDetailRow>
                <TransactionEntryDetailRow label="Date">
                    {isPending ? (
                        <Paragraph>{date.format('MMM DD, YYYY')}</Paragraph>
                    ) : (
                        <InputField
                            name="transaction-date"
                            type="date"
                            inputProps={{
                                variant: 'outline',
                                size: 'md',
                            }}
                            width={150}
                            value={date.toDateStr()}
                            onChange={async (d) => {
                                const newDate = dt(d);
                                setDate(newDate);

                                await updateTransactionEntry({
                                    variables: {
                                        transactionEntryId: transactionEntry.id,
                                        date: newDate.toDateStr(),
                                    },
                                });
                            }}
                        />
                    )}
                </TransactionEntryDetailRow>
                <TransactionEntryDetailRow label="Account">
                    <FlexRow
                        cursor="pointer"
                        gap={3}
                        onClick={() => {
                            if (transactionEntry.financialEntity.goal) {
                                showDrawer({
                                    drawerKey: 'goal-detail',
                                    entityId:
                                        transactionEntry.financialEntity.goal
                                            .id,
                                });
                            } else {
                                showDrawer({
                                    drawerKey: 'financial-entity-detail',
                                    entityId:
                                        transactionEntry.financialEntity.id,
                                });
                            }
                        }}
                    >
                        <InstitutionLogo
                            financialEntity={transactionEntry.financialEntity}
                        />
                        <FlexCol>
                            <Paragraph>
                                {transactionEntry.financialEntity.name}
                            </Paragraph>
                            {transactionEntry.financialEntity.mask && (
                                <Paragraph
                                    fontFamily="mono"
                                    color="gray.500"
                                    fontSize="sm"
                                >
                                    .... {transactionEntry.financialEntity.mask}
                                </Paragraph>
                            )}
                        </FlexCol>
                    </FlexRow>
                </TransactionEntryDetailRow>
                <TransactionEntryDetailRow
                    label="Original description"
                    divider={!isPending}
                >
                    <Paragraph>{transactionEntry.name}</Paragraph>
                </TransactionEntryDetailRow>
                {!isPending && (
                    <>
                        <Paragraph color="gray.500">Notes</Paragraph>
                        <FlexCol mt="4">
                            <Textarea
                                borderRadius={4}
                                placeholder="Add notes here..."
                                size="sm"
                                resize="none"
                                h="100px"
                                value={notes ?? ''}
                                onChange={(e) => {
                                    setNotes(e.target.value);
                                    setSaveNoteStatus('saving');
                                    handleUpdateNotes();
                                }}
                            />
                            <Text
                                mt={1}
                                textAlign="right"
                                fontSize="xs"
                                color={
                                    saveNoteStatus === 'error'
                                        ? 'red.500'
                                        : 'gray.500'
                                }
                                minH="1rem"
                            >
                                {saveNoteStatus === 'error' &&
                                    'Failed to save changes'}
                                {saveNoteStatus === 'saved' &&
                                    'All changes saved'}
                            </Text>
                        </FlexCol>
                        <FlexRow justifyContent="space-between" mt={4}>
                            <FlexCol>
                                <FlexCol>
                                    <Paragraph>Hide transaction</Paragraph>
                                    <Paragraph fontSize="sm" color="gray.500">
                                        Excludes transactions from spending
                                    </Paragraph>
                                </FlexCol>
                            </FlexCol>
                            <FlexRow>
                                <InputField
                                    label=""
                                    name="hide_transaction"
                                    type="yesNo"
                                    value={isHidden}
                                    inputProps={{ variant: 'base' }}
                                    onChange={hideTransactionEntry}
                                />
                            </FlexRow>
                        </FlexRow>
                        <ButtonWithIcon
                            colorScheme="red"
                            onClick={onClickDelete}
                            onClickEvent="Delete Transaction Entry Button Clicked"
                            variant="outline"
                            size="sm"
                            iconLeft={<Trash01 size="sm" />}
                            mt={6}
                        >
                            Delete this transaction
                        </ButtonWithIcon>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default TransactionEntryDetailsModal;
